import React, { useCallback, useMemo, useState } from 'react';
import { useCouponsRelayQuery, mapCouponsToICoupons } from '../../hooks/coupons/query/V2';
import { connect } from 'react-redux';
import { couponColumns } from '../../tableColumns/couponColumns';
import { findAccess, history } from '../../utils';
import { IAuth } from '../../ts/interface/admins.interface';
import Card from '@mui/material/Card';
import { PaginationTable } from '../../components/Redesing/table/pagination-table';
import AddIcon from '@mui/icons-material/Add';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { MovaticFieldGroupSelectFilter } from '../../components/Redesing/movatic-field-group';
import { SearchChip } from '../../ts/interface/components/table.interface';
import { FilterChips } from '../../ts/interface/components/redesing/table.interface';
import { useUpdateEffect } from '../../hooks/use-update-effect';
import { DEFAULT_ROWS_PER_PAGE } from '../../constants';
import { useSystemStore } from '../../store/systemStore';
import CouponForm from '../../components/coupons/CouponForm';

const filterOptions = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

const CouponIndex = ({
  systemLoaded,
  systemAccess,
  movaticAccess,
}: {
  systemLoaded: boolean;
  movaticAccess: number;
  systemAccess: number;
}) => {
  const [pageNo, setPageNo] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_ROWS_PER_PAGE);
  const [sortData, setSortData] = useState<string>('name');
  const [search, setSearch] = React.useState('');
  const [exportData, setExportData] = useState<boolean>(false);
  const [value, setValue] = React.useState<any[]>([{ label: 'Active', value: 'active' }]);
  const [chips, setChips] = useState<SearchChip[]>([
    {
      label: 'Status',
      field: 'status',
      value: 'active',
      displayValue: 'Active',
      disabled: false,
    },
  ]);
  const [filterChips, setFilterChips] = useState<FilterChips>({
    status: ['active'],
    locations: [],
  } as FilterChips);
  const [showAddModal, setShowAddModal] = useState(false);

  const systemOldId = useSystemStore((state) => state.systemInfo.oldId);
  const canManage = findAccess(systemAccess).includes('rates') || Boolean(movaticAccess);
  // `applicableToSystem` asks ant to return system-scoped coupons for this
  // system AND org-scoped coupons inherited from the system's organization.
  // Until the schema ships that read-action argument the hook falls back to
  // a plain `systemOldId` equality filter (see useCouponsRelay.ts).
  const {
    data: couponsData,
    isLoading,
    refetch,
  } = useCouponsRelayQuery(
    { applicableToSystem: systemOldId },
    { enabled: systemLoaded && !!systemOldId }
  );
  const coupons = useMemo(
    () => (couponsData ? mapCouponsToICoupons(couponsData) : []),
    [couponsData]
  );

  const filterCoupons = useMemo(() => {
    return coupons.filter((coupon) => {
      const couponStatus = coupon.active ? 'active' : 'inactive';
      // @ts-ignore
      if (filterChips.status.length > 0) {
        // @ts-ignore
        if (!filterChips.status.includes(couponStatus)) {
          return false;
        }
      }
      return true;
    });
  }, [filterChips.status, coupons]);

  const handleChipsUpdate = useCallback(() => {
    const filtersAux: FilterChips = {
      status: [],
    };
    chips.forEach((chip) => {
      switch (chip.field) {
        case 'status':
          // @ts-ignore
          filtersAux.status.push(chip.value);
          break;
        default:
          break;
      }
    });
    setFilterChips(filtersAux);
  }, [chips]);

  const handleStatusChange = useCallback((values: string[]): void => {
    setPageNo(0);
    setPageSize(DEFAULT_ROWS_PER_PAGE);
    setChips((prevChips) => {
      const valuesFound: string[] = [];

      const newChips = prevChips.filter((chip) => {
        if (chip.field !== 'status') {
          return true;
        }

        const found = values.includes(chip.value as string);

        if (found) {
          valuesFound.push(chip.value as string);
        }

        return found;
      });

      if (values.length === valuesFound.length) {
        return newChips;
      }

      values.forEach((value) => {
        if (!valuesFound.includes(value)) {
          const option = filterOptions.find(
            (option) => option.value.toString() === value
          );
          newChips.push({
            label: 'Status',
            field: 'status',
            value,
            displayValue: option!.label,
          });
        }
      });
      return newChips;
    });
  }, []);

  useUpdateEffect(() => {
    handleChipsUpdate();
  }, [chips, handleChipsUpdate]);

  const handleChipDelete = useCallback(
    (deletedGroup: SearchChip) => {
      setChips((prevChips) => {
        return prevChips.filter((group) => {
          return group.label !== deletedGroup.label;
        });
      });
      setValue([]);
      handleChipsUpdate();
    },
    [handleChipsUpdate]
  );

  return (
    <>
      <Card>
        <PaginationTable
          dataId={'billing-coupons'}
          padding={{ pt: 1, pl: 0, pr: 1 }}
          sortBy={sortData}
          sortDirection={'asc'}
          onSortChange={(sortBy) => setSortData(sortBy.key)}
          items={filterCoupons as any[]}
          noDataText="No Coupons Created Yet!"
          isLoading={isLoading}
          customKey={'gid'}
          onClickReturnRow
          onRowCLick={(row: any) => {
            const id = row?.gid ?? row?.id;
            const isOrg = row?.organizationId != null && row?.organizationId !== '';
            const target = isOrg
              ? `/organization/billing/coupon/${id}`
              : `/billing/coupon/${id}`;
            history.push(target);
          }}
          columns={couponColumns}
          rowsPerPage={pageSize}
          page={pageNo}
          onPageChange={(page, pageN: number) => {
            setPageNo(pageN);
          }}
          onRowsPerPageChange={(rowsPerPage) =>
            setPageSize(Number(rowsPerPage.target.value))
          }
          searchText={search}
          searchPlaceholder={`Search coupons`}
          handleSearch={(search) => setSearch(search.target.value)}
          buttons={[
            {
              label: 'Export',
              startIcon: <FileDownloadIcon />,
              onClick: () => setExportData(true),
            },
            {
              label: 'Add',
              startIcon: <AddIcon />,
              onClick: () => setShowAddModal(true),
              disabled: !canManage,
            },
          ]}
          exportData={exportData}
          csvFileName={'Coupons.csv'}
          chips={chips}
          handleChipDelete={handleChipDelete}
          onApplyFilter={() => {
            handleStatusChange([
              ...value
                .filter((option) =>
                  filterOptions.some((item) => item.label === option.label)
                )
                .map((item) => item.value.toString()),
            ]);
          }}
          spaceFilters={
            <MovaticFieldGroupSelectFilter
              label={'Status'}
              value={value}
              onChange={(event: React.SyntheticEvent, newValue: any[]) => {
                setValue([
                  ...newValue.filter((option) =>
                    filterOptions.some((item) => item.label === option.label)
                  ),
                ]);
              }}
              options={filterOptions}
            />
          }
          onExport={() => setExportData(false)}
        />
      </Card>
      {showAddModal && (
        <CouponForm
          scope="system"
          mode="create"
          renderMode="modal"
          open={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            refetch();
          }}
          systemLoaded={systemLoaded && !!systemOldId}
          systemOldId={systemOldId}
          cancelHref="/billing/coupons"
          successHref="/billing/coupons"
          saveDisabled={!canManage}
          title="Add Coupon"
        />
      )}
    </>
  );
};

export default connect(
  (state: {
    system: { isLoaded: boolean };
    admin: {
      admin: IAuth;
      systemAccess: number;
    };
  }) => ({
    systemLoaded: state.system.isLoaded,
    movaticAccess: state.admin.admin.admin.movaticAccess,
    systemAccess: state.admin.systemAccess,
  }),
  () => ({})
)(CouponIndex);
